import { useEffect } from 'react';

export interface SEOOptions {
    /** Page-specific title. The site name is appended automatically unless it's already present. */
    title: string;
    description?: string;
    /** Absolute or relative canonical URL. Defaults to the current location (without query string). */
    canonical?: string;
    image?: string;
    /** og:type — defaults to 'website'. Use 'business.business' for listing pages. */
    type?: string;
    /** Set true for pages that should never appear in search results (dashboards, auth forms, etc). */
    noindex?: boolean;
    /** Optional schema.org structured data to embed as a <script type="application/ld+json"> tag. */
    jsonLd?: object | null;
    /**
     * Set false to skip touching the document entirely for this call - e.g. a page
     * component rendered inside a preview dialog on top of another page, which must
     * not silently overwrite that page's title/canonical/JSON-LD (this hook mutates
     * the document head directly and has no cleanup-on-unmount, so a disabled render
     * never runs and never needs to "restore" anything). Defaults to true.
     */
    enabled?: boolean;
}

const SITE_NAME = 'چیستا';
const SITE_URL = 'https://cheesta.com.au';
const DEFAULT_DESCRIPTION = 'چیستا، جامع‌ترین مرجع معرفی کسب‌وکارهای ایرانیان استرالیا. کسب‌وکار خود را رایگان ثبت کنید یا رستوران، پزشک، وکیل و دیگر مشاغل ایرانی نزدیک خود را پیدا کنید.';

const setMetaTag = (attr: 'name' | 'property', key: string, content: string) => {
    let el = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
    if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    el.setAttribute('content', content);
};

const removeMetaTag = (attr: 'name' | 'property', key: string) => {
    document.head.querySelector(`meta[${attr}="${key}"]`)?.remove();
};

/**
 * Applies per-page SEO metadata (title, meta description, canonical, Open Graph/Twitter
 * card tags, robots directive, and optional JSON-LD structured data) directly to the
 * document head. This app is a client-rendered SPA with no server-side rendering, so this
 * is the mechanism available for giving each route its own title/description instead of
 * the single static one baked into public/index.html — Googlebot (and, increasingly,
 * other engines) reads the DOM after JS execution, so this is picked up the same way a
 * library like react-helmet would do it, just without adding a dependency.
 */
export const useSEO = ({ title, description, canonical, image, type = 'website', noindex, jsonLd, enabled = true }: SEOOptions) => {
    useEffect(() => {
        if (!enabled) return;
        const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
        document.title = fullTitle;

        const desc = description || DEFAULT_DESCRIPTION;
        setMetaTag('name', 'description', desc);
        setMetaTag('property', 'og:description', desc);
        setMetaTag('name', 'twitter:description', desc);

        setMetaTag('property', 'og:title', fullTitle);
        setMetaTag('name', 'twitter:title', fullTitle);
        setMetaTag('property', 'og:site_name', SITE_NAME);
        setMetaTag('property', 'og:type', type);
        setMetaTag('name', 'twitter:card', image ? 'summary_large_image' : 'summary');

        if (image) {
            const absoluteImage = image.startsWith('http') ? image : `${SITE_URL}${image}`;
            setMetaTag('property', 'og:image', absoluteImage);
            setMetaTag('name', 'twitter:image', absoluteImage);
        } else {
            removeMetaTag('property', 'og:image');
            removeMetaTag('name', 'twitter:image');
        }

        const canonicalUrl = canonical
            ? (canonical.startsWith('http') ? canonical : `${SITE_URL}${canonical}`)
            : `${SITE_URL}${window.location.pathname}`;
        setMetaTag('property', 'og:url', canonicalUrl);

        let linkEl = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
        if (!linkEl) {
            linkEl = document.createElement('link');
            linkEl.setAttribute('rel', 'canonical');
            document.head.appendChild(linkEl);
        }
        linkEl.setAttribute('href', canonicalUrl);

        if (noindex) {
            setMetaTag('name', 'robots', 'noindex, nofollow');
        } else {
            removeMetaTag('name', 'robots');
        }

        const scriptId = 'seo-structured-data';
        let script = document.getElementById(scriptId) as HTMLScriptElement | null;
        if (jsonLd) {
            if (!script) {
                script = document.createElement('script');
                script.id = scriptId;
                script.type = 'application/ld+json';
                document.head.appendChild(script);
            }
            script.textContent = JSON.stringify(jsonLd);
        } else if (script) {
            script.remove();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, title, description, canonical, image, type, noindex, JSON.stringify(jsonLd)]);
};

export default useSEO;
